/* ================================================================
   LFJ GALLERY ROOM — Redesign Checkpoint 5 (Part D §5)
   ----------------------------------------------------------------
   Three responsibilities:

   1. DATA — fetches gallery.json and worked-with.json asynchronously,
      renders cards into the horizontal strip, renders the WORKED WITH
      grid. Falls back gracefully if either file is missing.

   2. FILTER + SORT — category tabs filter allItems, sort button
      toggles latest/earliest. Both re-render the strip and scroll
      it back to the left. Smooth UX on category switch.

   3. MODAL — pixel lightbox. Opens on card click with image (picture/
      design/commission) or YouTube nocookie iframe (video). Prev/next
      navigation through the current filtered set. Escape to close,
      left/right arrow keys to navigate. Focus trapped inside modal
      while open. Iframe src cleared on close to stop playback.

   Engine pattern: safeRun + __lfjGalleryBound init-guard, same as
   every other engine in this theme. Every external data read is
   wrapped in its own safeRun so a bad gallery.json never crashes the
   worked-with section, and vice versa.

   IDs and classes read from the DOM are load-bearing contracts with
   templates/page.gallery.liquid and assets/lfj-gallery.css.
================================================================ */

(function () {
  if (window.__lfjGalleryBound) return;
  window.__lfjGalleryBound = true;

  function safeRun(label, fn) {
    try { fn(); }
    catch (e) { console.warn('[LFJ:gallery] ' + label, e); }
  }

  /* ── Base URL for asset files ── */
  var GALLERY_JSON   = (window.LF_CONFIG && window.LF_CONFIG.themeAssetsBase)
    ? window.LF_CONFIG.themeAssetsBase + 'gallery.json'
    : '/cdn/shop/t/1/assets/gallery.json';
  var WORKED_WITH_JSON = (window.LF_CONFIG && window.LF_CONFIG.themeAssetsBase)
    ? window.LF_CONFIG.themeAssetsBase + 'worked-with.json'
    : '/cdn/shop/t/1/assets/worked-with.json';

  /* -- Provide override via data attribute if present (more reliable
     than guessing the CDN path) */
  var rootEl = document.getElementById('lfjGalleryRoot');
  if (rootEl) {
    if (rootEl.dataset.galleryUrl)   GALLERY_JSON   = rootEl.dataset.galleryUrl;
    if (rootEl.dataset.workedWithUrl) WORKED_WITH_JSON = rootEl.dataset.workedWithUrl;
  }

  /* ── State ── */
  var allItems      = [];
  var filteredItems = [];
  var activeCategory = 'all';
  var activeSort     = 'latest';
  var modalIndex     = -1;

  /* ── DOM refs ── */
  var strip     = document.getElementById('lfjGalStrip');
  var emptyEl   = document.getElementById('lfjGalEmpty');
  var catTabs   = document.querySelectorAll('.lfj-gal-cat-tab');
  var sortBtn   = document.getElementById('lfjGalSort');
  var wwGrid    = document.getElementById('lfjGalWWGrid');
  var modal     = document.getElementById('lfjGalModal');
  var modalContent = document.getElementById('lfjGalModalContent');
  var modalTitle   = document.getElementById('lfjGalModalTitle');
  var modalYear    = document.getElementById('lfjGalModalYear');
  var modalCounter = document.getElementById('lfjGalModalCounter');
  var modalClose   = document.getElementById('lfjGalModalClose');
  var modalPrev    = document.getElementById('lfjGalModalPrev');
  var modalNext    = document.getElementById('lfjGalModalNext');

  /* ── Helpers ── */
  function sortItems(items) {
    return items.slice().sort(function (a, b) {
      var ai = typeof a.sort_index === 'number' ? a.sort_index : 0;
      var bi = typeof b.sort_index === 'number' ? b.sort_index : 0;
      return activeSort === 'latest' ? bi - ai : ai - bi;
    });
  }

  function filterItems() {
    var base = activeCategory === 'all'
      ? allItems
      : allItems.filter(function (it) { return it.category === activeCategory; });
    return sortItems(base);
  }

  /* Encode a YouTube video ID for nocookie embed */
  function ytSrc(id) {
    return 'https://www.youtube-nocookie.com/embed/'
      + encodeURIComponent(id)
      + '?autoplay=1&rel=0&modestbranding=1';
  }

  /* ── Card rendering ── */
  function buildCardHTML(item, index) {
    /* Checkpoint 65 — with no thumbnail chosen, the Discord bot saves the
       uploaded .mp4's own URL as the "thumbnail", which an <img> can't show
       (a broken-image icon). Treat a video URL as "no thumbnail" so the
       existing random-frame fallback takes over. */
    var thumbIsVideoFile = /\.(mp4|mov|webm|m4v)(\?|#|$)/i.test(item.thumbnail || '');
    var hasThumbnail = item.thumbnail && item.thumbnail.length > 0 && !thumbIsVideoFile;
    var canFallback = !hasThumbnail && item.type === 'video' && (item.src || item.videoUrl);
    var thumbCls = hasThumbnail ? '' : ' lfj-gal-card-thumb--empty';
    var thumbInner = hasThumbnail
      ? '<img src="' + escAttr(item.thumbnail) + '" alt="' + escAttr(item.title) + '" loading="lazy">'
      : canFallback
        /* Checkpoint 59 — video item, no thumbnail: grab a random frame
           instead of a static placeholder mark (see lfj-thumb-fallback.js). */
        ? '<img data-need-thumb="1" data-key="' + escAttr(item.id) + '" alt="' + escAttr(item.title) + '" loading="lazy">'
        : '<span class="lfj-gal-placeholder-mark" aria-hidden="true">▪</span>';

    var playGlyph = (item.type === 'youtube' || item.type === 'video')
      ? '<div class="lfj-gal-play-glyph" aria-hidden="true"></div>'
      : '';

    var badge = activeCategory === 'all'
      ? '<span class="lfj-gal-cat-badge">' + escHTML(item.category.toUpperCase()) + '</span>'
      : '';

    var yearMeta = item.year ? escHTML(item.year) : '';
    var collab = item.collab ? ' · ' + escHTML(item.collab) : '';

    return '<button class="lfj-gal-card" data-index="' + index + '"'
      + ' aria-label="' + escAttr(item.title) + '">'
      + '<div class="lfj-gal-card-thumb' + thumbCls + '">'
      + thumbInner + playGlyph + badge
      + '</div>'
      + '<div class="lfj-gal-card-bar">'
      + '<span class="lfj-gal-card-title">' + escHTML(item.title) + collab + '</span>'
      + '<span class="lfj-gal-card-meta">' + yearMeta + '</span>'
      + '</div>'
      + '</button>';
  }

  function renderCards() {
    safeRun('renderCards', function () {
      if (!strip) return;
      filteredItems = filterItems();

      if (filteredItems.length === 0) {
        strip.innerHTML = '';
        if (emptyEl) emptyEl.removeAttribute('hidden');
        return;
      }

      if (emptyEl) emptyEl.setAttribute('hidden', '');

      var html = filteredItems.map(function (item, i) {
        return buildCardHTML(item, i);
      }).join('');

      strip.innerHTML = html;

      /* Checkpoint 59 — fill in a random-frame thumbnail for any video
         card that had no thumbnail value (see buildCardHTML above and
         assets/lfj-thumb-fallback.js). */
      if (window.LFJ_THUMB_FALLBACK) {
        strip.querySelectorAll('img[data-need-thumb]').forEach(function (img) {
          var key = img.getAttribute('data-key');
          var entry = filteredItems.filter(function (it) { return it.id === key; })[0];
          if (entry) window.LFJ_THUMB_FALLBACK.apply(img, entry);
        });
      }

      /* Scroll to start on re-render */
      var wrap = strip.parentElement;
      if (wrap) wrap.scrollLeft = 0;

      /* Wire card clicks */
      var cards = strip.querySelectorAll('.lfj-gal-card');
      cards.forEach(function (card) {
        card.addEventListener('click', function () {
          safeRun('card-click', function () {
            var idx = parseInt(card.dataset.index, 10);
            openModal(idx);
          });
        });
      });
    });
  }

  /* ── Category tabs ── */
  function setCategory(cat) {
    activeCategory = cat;
    catTabs.forEach(function (tab) {
      if (tab.dataset.cat === cat) tab.classList.add('is-active');
      else tab.classList.remove('is-active');
    });
    renderCards();
  }

  catTabs.forEach(function (tab) {
    tab.addEventListener('click', function () {
      safeRun('cat-tab', function () { setCategory(tab.dataset.cat); });
    });
  });

  /* ── Sort button ── */
  if (sortBtn) {
    sortBtn.addEventListener('click', function () {
      safeRun('sort', function () {
        activeSort = activeSort === 'latest' ? 'earliest' : 'latest';
        sortBtn.textContent = activeSort === 'latest' ? '↓ LATEST' : '↑ EARLIEST';
        renderCards();
      });
    });
  }

  /* ── Modal ── */
  function openModal(index) {
    safeRun('openModal', function () {
      if (!modal || !filteredItems.length) return;
      modalIndex = Math.max(0, Math.min(index, filteredItems.length - 1));
      renderModal();
      modal.removeAttribute('hidden');
      modal.setAttribute('aria-hidden', 'false');
      if (modalClose) modalClose.focus();
      document.body.style.overflow = 'hidden';
    });
  }

  function renderModal() {
    safeRun('renderModal', function () {
      if (modalIndex < 0 || modalIndex >= filteredItems.length) return;
      var item = filteredItems[modalIndex];

      /* Title + year */
      if (modalTitle)   modalTitle.textContent   = item.title || '';
      if (modalYear)    modalYear.textContent     = item.year  || '';
      if (modalCounter) modalCounter.textContent  =
        (modalIndex + 1) + ' / ' + filteredItems.length;

      /* Nav button states */
      if (modalPrev) modalPrev.disabled = modalIndex === 0;
      if (modalNext) modalNext.disabled = modalIndex === filteredItems.length - 1;

      /* Content */
      if (!modalContent) return;
      /* Clear previous iframe to stop playback */
      var oldIframe = modalContent.querySelector('iframe');
      if (oldIframe) oldIframe.src = '';
      var oldVideo = modalContent.querySelector('video');
      if (oldVideo) { try { oldVideo.pause(); } catch (e) {} }

      if (item.type === 'youtube' && item.src) {
        modalContent.innerHTML =
          '<div class="lfj-gal-modal-video-wrap">'
          + '<iframe id="lfjGalModalIframe" src="' + ytSrc(item.src) + '"'
          + ' allow="autoplay; encrypted-media; picture-in-picture"'
          + ' allowfullscreen title="' + escAttr(item.title) + '"></iframe>'
          + '</div>';
      } else if (item.type === 'video' && item.src) {
        /* Checkpoint 65 — the bot's `type:"video"` (an uploaded file). This
           used to fall through to an <img src=".mp4"> = broken image. */
        modalContent.innerHTML =
          '<div class="lfj-gal-modal-video-wrap">'
          + '<video class="lfj-gal-modal-video" src="' + escAttr(item.src) + '" controls playsinline autoplay'
          + ' aria-label="' + escAttr(item.title) + '"></video>'
          + '</div>';
      } else if (item.type === 'link' && item.src) {
        /* Checkpoint 65 — the bot's `type:"link"` (Instagram / other page).
           Nothing to embed; offer a real link instead of a broken <img>.
           Only http(s) is linked, so a bad value can't become javascript:. */
        var safeHref = /^https?:\/\//i.test(item.src) ? item.src : '';
        modalContent.innerHTML = safeHref
          ? '<div class="lfj-gal-modal-linkcard">'
            + '<a href="' + escAttr(safeHref) + '" target="_blank" rel="noopener noreferrer">OPEN LINK \u2197</a>'
            + '</div>'
          : '<div class="lfj-gal-modal-linkcard">NO SOURCE CONFIGURED</div>';
      } else if (item.src) {
        modalContent.innerHTML =
          '<img src="' + escAttr(item.src) + '" alt="' + escAttr(item.title) + '">';
      } else {
        /* No src yet — show placeholder */
        modalContent.innerHTML =
          '<div style="padding:40px;font-family:var(--font-pixel);'
          + 'font-size:7px;color:var(--lfj-screen-dim);text-align:center;'
          + 'letter-spacing:.04em">NO SOURCE CONFIGURED</div>';
      }
    });
  }

  function closeModal() {
    safeRun('closeModal', function () {
      if (!modal) return;
      /* Clear iframe to stop YT playback */
      if (modalContent) {
        var iframe = modalContent.querySelector('iframe');
        if (iframe) iframe.src = '';
        var vid = modalContent.querySelector('video');
        if (vid) { try { vid.pause(); } catch (e) {} modalContent.innerHTML = ''; }
      }
      modal.setAttribute('hidden', '');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      modalIndex = -1;
    });
  }

  function stepModal(dir) {
    safeRun('stepModal', function () {
      var next = modalIndex + dir;
      if (next < 0 || next >= filteredItems.length) return;
      /* Clear old iframe before switching */
      if (modalContent) {
        var iframe = modalContent.querySelector('iframe');
        if (iframe) iframe.src = '';
      }
      modalIndex = next;
      renderModal();
    });
  }

  if (modalClose) modalClose.addEventListener('click', closeModal);
  if (modalPrev)  modalPrev.addEventListener('click',  function () { stepModal(-1); });
  if (modalNext)  modalNext.addEventListener('click',  function () { stepModal(1); });

  /* Close on overlay click (clicking the darkened backdrop) */
  if (modal) {
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal();
    });
  }

  /* Keyboard: Escape = close, arrows = prev/next */
  document.addEventListener('keydown', function (e) {
    safeRun('keydown', function () {
      if (!modal || modal.hasAttribute('hidden')) return;
      if (e.key === 'Escape')      { e.preventDefault(); closeModal(); }
      if (e.key === 'ArrowLeft')   { e.preventDefault(); stepModal(-1); }
      if (e.key === 'ArrowRight')  { e.preventDefault(); stepModal(1); }
    });
  });

  /* ── WORKED WITH renderer ── */
  function renderWorkedWith(collaborators) {
    safeRun('renderWW', function () {
      if (!wwGrid || !collaborators || !collaborators.length) return;
      wwGrid.innerHTML = collaborators.map(function (c) {
        var name = c.name || 'UNKNOWN';
        var handle = c.handle ? ' · ' + c.handle : '';
        if (c.url) {
          return '<a class="lfj-gal-ww-item" href="' + escAttr(c.url)
            + '" target="_blank" rel="noopener noreferrer">'
            + escHTML(name + handle) + '</a>';
        }
        return '<span class="lfj-gal-ww-item">' + escHTML(name + handle) + '</span>';
      }).join('');
    });
  }

  /* ── Escaping helpers ── */
  function escHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function escAttr(str) { return escHTML(str).replace(/'/g, '&#39;'); }

  /* ── Fetch + init ── */
  function fetchJSON(url) {
    return fetch(url + '?v=' + Date.now())
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      });
  }

  function init() {
    /* Gallery items */
    fetchJSON(GALLERY_JSON)
      .then(function (data) {
        safeRun('init-gallery', function () {
          var items = Array.isArray(data) ? data : [];
          /* Phase 1 (Checkpoint 56) — gallery.json can now be shared
             with future destination-consumers (e.g. Commission)
             without leaking their exclusive items onto this page.
             No `destination` field at all = renders here exactly as
             before (backward compatible). */
          allItems = items.filter(function (it) {
            if (!Array.isArray(it.destination)) return true;
            return it.destination.indexOf('portfolio') > -1 || it.destination.indexOf('gallery') > -1 || it.destination.indexOf('all') > -1;
          });
          renderCards();
        });
      })
      .catch(function (err) {
        console.warn('[LFJ:gallery] gallery.json fetch failed:', err);
        if (emptyEl) emptyEl.removeAttribute('hidden');
      });

    /* Worked-with */
    fetchJSON(WORKED_WITH_JSON)
      .then(function (data) {
        safeRun('init-ww', function () {
          renderWorkedWith(Array.isArray(data) ? data : []);
        });
      })
      .catch(function (err) {
        console.warn('[LFJ:gallery] worked-with.json fetch failed:', err);
      });

    /* Activate default category tab */
    setCategory('all');
  }

  document.addEventListener('DOMContentLoaded', function () {
    safeRun('DOMContentLoaded', init);
  });

}());
